import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';

interface FileTransferContextType {
  transferFile: (file: File) => void;
  registerFileHandler: (handler: (file: File) => void) => () => void;
}

const FileTransferContext = createContext<FileTransferContextType>({
  transferFile: () => {},
  registerFileHandler: () => () => {},
});

export const FileTransferProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use a ref for the active handler
  const activeHandlerRef = useRef<((file: File) => void) | null>(null);

  const transferFile = useCallback((file: File) => {
    console.log("🔄 Transferring file:", file.name);
    
    if (activeHandlerRef.current) {
      console.log("✅ Handler found, calling with file");
      activeHandlerRef.current(file);
    } else {
      console.warn("⚠️ No file handler registered");
    }
  }, []);
  
  const registerFileHandler = useCallback((handler: (file: File) => void) => {
    console.log("📝 Registering new file handler");
    activeHandlerRef.current = handler;
    
    // Return cleanup function
    return () => {
      console.log("🧹 Cleaning up handler registration");
      if (activeHandlerRef.current === handler) {
        activeHandlerRef.current = null;
      }
    };
  }, []);
  
  const contextValue = useMemo(() => ({
    transferFile,
    registerFileHandler
  }), [transferFile, registerFileHandler]);
  
  return (
    <FileTransferContext.Provider value={contextValue}>
      {children}
    </FileTransferContext.Provider>
  );
};

export const useFileTransfer = () => useContext(FileTransferContext);